import { Check, LoaderCircle, Save, Sparkles, X } from "lucide-react";
import { PointerEvent, useMemo, useRef, useState } from "react";

export type OcrRect = { x: number; y: number; width: number; height: number };
export type OcrRegion = { text: string; confidence?: number; box: OcrRect };
export type QaIssue = {
  type: "missing_text" | "chinese_remaining" | "glossary_mismatch" | "low_text";
  message: string;
  region?: OcrRegion;
  outputRegion?: OcrRegion;
  sourceText?: string;
  expectedText?: string;
  key?: string;
  dismissed?: boolean;
};
export type QaReport = {
  status: "passed" | "needs_review";
  checkedAt: string;
  original: { width: number; height: number; text: string; regions: OcrRegion[] };
  output: { width: number; height: number; text: string; regions: OcrRegion[] };
  issues: QaIssue[];
};

type Props = {
  batchId: string;
  item: { id: string; name: string; type: "image/png" | "image/jpeg" };
  report: QaReport;
  originalSrc: string;
  outputSrc: string;
  onClose: () => void;
  onSaved: () => void;
  onReportUpdated: (report: QaReport) => void;
};

async function api<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(path, options);
  let body: { error?: string } & Partial<T> = {};
  try { body = await response.json() as typeof body; } catch { /* non-JSON failure */ }
  if (!response.ok) throw new Error(body.error || `Request failed (${response.status})`);
  return body as T;
}

function encodeCanvas(canvas: HTMLCanvasElement, type: Props["item"]["type"]): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob(
    (blob) => blob ? resolve(blob) : reject(new Error("Could not encode corrected image")),
    type,
    type === "image/jpeg" ? 0.94 : undefined,
  ));
}

function cleanSourceText(text: string): string {
  return text.replace(/\s+\d{1,2}:\d{2}(?:\s*[AP]M)?\s*$/i, "").trim();
}

function expandedRect(region: OcrRegion | undefined, width: number, height: number): OcrRect {
  if (!region) return { x: width * 0.15, y: height * 0.4, width: width * 0.7, height: Math.max(60, height * 0.06) };
  const padding = Math.max(4, region.box.height * 0.25);
  const x = Math.max(0, region.box.x - padding);
  const y = Math.max(0, region.box.y - padding);
  return {
    x,
    y,
    width: Math.min(width - x, Math.max(region.box.width * 2.2, 220)),
    height: Math.min(height - y, Math.max(region.box.height * 2.4, 64)),
  };
}

function wrapText(context: CanvasRenderingContext2D, text: string, width: number): string[] {
  const words = text.trim().split(/\s+/);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (current && context.measureText(candidate).width > width) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function sampleBackground(context: CanvasRenderingContext2D, rect: OcrRect, canvas: HTMLCanvasElement): { fill: string; text: string } {
  const points = [
    [rect.x - 3, rect.y + rect.height / 2],
    [rect.x + rect.width + 3, rect.y + rect.height / 2],
    [rect.x + rect.width / 2, rect.y - 3],
    [rect.x + rect.width / 2, rect.y + rect.height + 3],
  ];
  const colors = points.map(([rawX, rawY]) => {
    const x = Math.max(0, Math.min(canvas.width - 1, Math.round(rawX)));
    const y = Math.max(0, Math.min(canvas.height - 1, Math.round(rawY)));
    return context.getImageData(x, y, 1, 1).data;
  });
  const rgb = [0, 1, 2].map((channel) => Math.round(colors.reduce((sum, color) => sum + color[channel], 0) / colors.length));
  const luminance = rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114;
  return { fill: `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`, text: luminance < 135 ? "#ffffff" : "#17191d" };
}

type ActionableIssue = { issue: QaIssue; reportIndex: number };
type IssueDraft = { sourceText: string; english: string; selection: OcrRect };

function issueDraftKey(entry: ActionableIssue): string {
  return entry.issue.key || `${entry.issue.type}-${entry.reportIndex}`;
}

function makeDraft(issue: QaIssue, width: number, height: number): IssueDraft {
  return {
    sourceText: issue.sourceText ?? cleanSourceText(issue.region?.text ?? ""),
    english: issue.expectedText ?? "",
    selection: expandedRect(issue.outputRegion ?? issue.region, width, height),
  };
}

function isFixable(entry: ActionableIssue): boolean {
  return entry.issue.type !== "low_text" && Boolean(entry.issue.region || entry.issue.outputRegion);
}

export function FixTextModal({ batchId, item, report, originalSrc, outputSrc, onClose, onSaved, onReportUpdated }: Props) {
  const [workingReport, setWorkingReport] = useState(report);
  const imageWidth = workingReport.output.width || workingReport.original.width || 1;
  const imageHeight = workingReport.output.height || workingReport.original.height || 1;
  const actionable = useMemo<ActionableIssue[]>(() => workingReport.issues.map((issue, reportIndex) => ({ issue, reportIndex })).filter((entry) => !entry.issue.dismissed), [workingReport]);
  const [issueIndex, setIssueIndex] = useState(0);
  const currentEntry = actionable[issueIndex];
  const issue = currentEntry?.issue;
  const sourceRegion = issue?.region;
  const currentKey = currentEntry ? issueDraftKey(currentEntry) : "";
  const [drafts, setDrafts] = useState<Record<string, IssueDraft>>(() => Object.fromEntries(
    report.issues.map((issue, reportIndex) => ({ issue, reportIndex })).filter((entry) => !entry.issue.dismissed).map((entry) => [issueDraftKey(entry), makeDraft(entry.issue, report.output.width || report.original.width || 1, report.output.height || report.original.height || 1)]),
  ));
  const draft = drafts[currentKey];
  const sourceText = draft?.sourceText ?? "";
  const english = draft?.english ?? "";
  const selection = draft?.selection ?? expandedRect(undefined, imageWidth, imageHeight);
  const fixableEntries = actionable.filter(isFixable);
  const readyCount = fixableEntries.filter((entry) => {
    const candidate = drafts[issueDraftKey(entry)];
    return Boolean(candidate?.english.trim() && candidate.selection.width >= 8 && candidate.selection.height >= 8);
  }).length;
  const allReady = fixableEntries.length > 0 && readyCount === fixableEntries.length;
  const needsTranslation = fixableEntries.some((entry) => {
    const candidate = drafts[issueDraftKey(entry)];
    return Boolean(candidate?.sourceText.trim() && !candidate.english.trim());
  });
  const [busy, setBusy] = useState<"translate" | "translate_all" | "save" | "dismiss" | null>(null);
  const [error, setError] = useState("");
  const outputImage = useRef<HTMLImageElement>(null);
  const dragStart = useRef<{ x: number; y: number } | null>(null);

  function chooseIssue(index: number) {
    setIssueIndex(index);
    setError("");
  }

  function updateDraft(patch: Partial<IssueDraft>) {
    if (!currentKey) return;
    setDrafts((current) => ({
      ...current,
      [currentKey]: { ...(current[currentKey] ?? makeDraft(issue!, imageWidth, imageHeight)), ...patch },
    }));
  }

  async function dismissIssue() {
    if (!issue?.key) return;
    setBusy("dismiss");
    setError("");
    try {
      const nextReport = await api<QaReport>(`/api/item/${batchId}/${item.id}/qa`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ issueKey: issue.key }),
      });
      setWorkingReport(nextReport);
      onReportUpdated(nextReport);
      const remaining = nextReport.issues.map((entry, reportIndex) => ({ issue: entry, reportIndex })).filter((entry) => !entry.issue.dismissed);
      if (!remaining.length) {
        onClose();
        return;
      }
      setDrafts((current) => {
        const next = { ...current };
        for (const entry of remaining) {
          const key = issueDraftKey(entry);
          if (!next[key]) next[key] = makeDraft(entry.issue, imageWidth, imageHeight);
        }
        return next;
      });
      const nextIndex = Math.min(issueIndex, remaining.length - 1);
      setIssueIndex(nextIndex);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not dismiss this issue");
    } finally {
      setBusy(null);
    }
  }

  function imagePoint(event: PointerEvent<HTMLDivElement>): { x: number; y: number } | null {
    const image = outputImage.current;
    if (!image) return null;
    const bounds = image.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) return null;
    return {
      x: ((event.clientX - bounds.left) / bounds.width) * imageWidth,
      y: ((event.clientY - bounds.top) / bounds.height) * imageHeight,
    };
  }

  function pointerDown(event: PointerEvent<HTMLDivElement>) {
    const point = imagePoint(event);
    if (!point) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStart.current = point;
    updateDraft({ selection: { x: point.x, y: point.y, width: 1, height: 1 } });
  }

  function pointerMove(event: PointerEvent<HTMLDivElement>) {
    if (!dragStart.current) return;
    const point = imagePoint(event);
    if (!point) return;
    updateDraft({ selection: {
      x: Math.min(dragStart.current.x, point.x),
      y: Math.min(dragStart.current.y, point.y),
      width: Math.abs(point.x - dragStart.current.x),
      height: Math.abs(point.y - dragStart.current.y),
    } });
  }

  function pointerUp() {
    dragStart.current = null;
  }

  async function translate() {
    if (!sourceText.trim()) return;
    setBusy("translate");
    setError("");
    try {
      const result = await api<{ text: string }>("/api/translate-text", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: sourceText }),
      });
      updateDraft({ english: result.text });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not translate text");
    } finally {
      setBusy(null);
    }
  }

  async function translateAll() {
    if (!needsTranslation) return;
    setBusy("translate_all");
    setError("");
    const nextDrafts = { ...drafts };
    try {
      for (const entry of fixableEntries) {
        const key = issueDraftKey(entry);
        const candidate = nextDrafts[key] ?? makeDraft(entry.issue, imageWidth, imageHeight);
        if (!candidate.sourceText.trim() || candidate.english.trim()) continue;
        const result = await api<{ text: string }>("/api/translate-text", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: candidate.sourceText }),
        });
        nextDrafts[key] = { ...candidate, english: result.text };
        setDrafts({ ...nextDrafts });
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not translate every issue");
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    if (!allReady) {
      setError("Translate or enter English for every fixable tab before saving all corrections.");
      return;
    }
    setBusy("save");
    setError("");
    try {
      const response = await fetch(`${outputSrc}${outputSrc.includes("?") ? "&" : "?"}v=${Date.now()}`, { cache: "no-store" });
      if (!response.ok) throw new Error("Could not load translated image");
      const bitmap = await createImageBitmap(await response.blob());
      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const context = canvas.getContext("2d");
      if (!context) throw new Error("Browser image editing is unavailable");
      context.drawImage(bitmap, 0, 0);
      bitmap.close();

      for (const [correctionIndex, entry] of fixableEntries.entries()) {
        const candidate = drafts[issueDraftKey(entry)];
        const rectX = Math.max(0, Math.min(canvas.width - 1, candidate.selection.x));
        const rectY = Math.max(0, Math.min(canvas.height - 1, candidate.selection.y));
        const rect = {
          x: rectX,
          y: rectY,
          width: Math.max(8, Math.min(canvas.width - rectX, candidate.selection.width)),
          height: Math.max(8, Math.min(canvas.height - rectY, candidate.selection.height)),
        };
        const colors = sampleBackground(context, rect, canvas);
        if (entry.issue.type === "chinese_remaining" || entry.issue.type === "glossary_mismatch") {
          context.fillStyle = colors.fill;
          context.fillRect(rect.x, rect.y, rect.width, rect.height);
        }
        const padding = Math.max(4, Math.min(14, rect.height * 0.12));
        const availableWidth = rect.width - padding * 2;
        const availableHeight = rect.height - padding * 2;
        let fontSize = Math.min(48, Math.max(10, rect.height * 0.55));
        let lines: string[] = [];
        for (; fontSize >= 9; fontSize -= 1) {
          context.font = `500 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
          lines = wrapText(context, candidate.english, availableWidth);
          if (lines.length * fontSize * 1.2 <= availableHeight) break;
        }
        if (!lines.length || fontSize < 9) throw new Error(`Draw a larger text area for correction ${correctionIndex + 1} so the English can fit`);
        context.fillStyle = colors.text;
        context.textBaseline = "top";
        context.font = `500 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
        lines.forEach((line, lineIndex) => context.fillText(line, rect.x + padding, rect.y + padding + lineIndex * fontSize * 1.2));
      }

      const blob = await encodeCanvas(canvas, item.type);
      canvas.width = 1;
      canvas.height = 1;
      if (blob.size > 3_000_000) throw new Error("Corrected image exceeds the 3 MB limit");
      const form = new FormData();
      form.set("file", new File([blob], item.name, { type: item.type }), item.name);
      await api(`/api/item/${batchId}/${item.id}/fix-output`, { method: "POST", body: form });
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save corrected image");
    } finally {
      setBusy(null);
    }
  }

  const overlayStyle = {
    left: `${(selection.x / imageWidth) * 100}%`,
    top: `${(selection.y / imageHeight) * 100}%`,
    width: `${(selection.width / imageWidth) * 100}%`,
    height: `${(selection.height / imageHeight) * 100}%`,
  };
  const sourceStyle = sourceRegion ? {
    left: `${(sourceRegion.box.x / Math.max(1, workingReport.original.width)) * 100}%`,
    top: `${(sourceRegion.box.y / Math.max(1, workingReport.original.height)) * 100}%`,
    width: `${(sourceRegion.box.width / Math.max(1, workingReport.original.width)) * 100}%`,
    height: `${(sourceRegion.box.height / Math.max(1, workingReport.original.height)) * 100}%`,
  } : undefined;

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="fix-modal" role="dialog" aria-modal="true" aria-label={`Fix text in ${item.name}`}>
        <header>
          <div><div className="eyebrow">OCR-assisted correction</div><strong>{item.name}</strong></div>
          <button className="icon-button" onClick={onClose}><X size={18} /></button>
        </header>

        {actionable.length > 0 && (
          <div className="issue-tabs">
            {actionable.map(({ issue: entry, reportIndex }, index) => (
              <button className={`${index === issueIndex ? "active" : ""} ${drafts[issueDraftKey({ issue: entry, reportIndex })]?.english.trim() ? "ready" : ""}`} onClick={() => chooseIssue(index)} key={entry.key || `${entry.type}-${reportIndex}`}>
                {entry.type === "missing_text" ? "Missing" : entry.type === "chinese_remaining" ? "Chinese remains" : entry.type === "glossary_mismatch" ? "Glossary" : "Low text"} {index + 1}
                {drafts[issueDraftKey({ issue: entry, reportIndex })]?.english.trim() && <Check size={12} />}
              </button>
            ))}
          </div>
        )}

        <div className="fix-grid">
          <div className="fix-panel">
            <div className="fix-panel-label"><span>Original</span><small>OCR source region</small></div>
            <div className="fix-image-stage">
              <img src={originalSrc} alt="Original" />
              {sourceStyle && <span className="source-box" style={sourceStyle} />}
            </div>
          </div>
          <div className="fix-panel">
            <div className="fix-panel-label"><span>English output</span><small>Drag to choose where English should appear</small></div>
            <div className="fix-image-stage editable" onPointerDown={pointerDown} onPointerMove={pointerMove} onPointerUp={pointerUp} onPointerCancel={pointerUp}>
              <img ref={outputImage} src={outputSrc} alt="Translated output" draggable={false} />
              <span className="target-box" style={overlayStyle} />
            </div>
          </div>
        </div>

        <div className="fix-form">
          {issue?.type === "low_text" && <div className="issue-explanation">Google detected substantially less text in the output. Review both images, then mark it as not an issue if the translation looks complete.</div>}
          <label>Chinese detected<textarea value={sourceText} onChange={(event) => updateDraft({ sourceText: event.target.value })} rows={2} /></label>
          <button className="button secondary translate-text-button" onClick={translate} disabled={!sourceText.trim() || Boolean(busy)}>
            {busy === "translate" ? <LoaderCircle className="spin" size={17} /> : <Sparkles size={17} />} Translate text
          </button>
          <label>English to render<textarea value={english} onChange={(event) => updateDraft({ english: event.target.value })} rows={3} placeholder="Translate, then edit the English if needed" /></label>
          {error && <div className="form-error">{error}</div>}
        </div>

        <footer>
          <span><Check size={15} /> {readyCount}/{fixableEntries.length} corrections ready · one backup before saving</span>
          <div><button className="button secondary dismiss-issue-button" onClick={dismissIssue} disabled={!issue?.key || Boolean(busy)}>{busy === "dismiss" ? <LoaderCircle className="spin" size={17} /> : <X size={17} />} Not an issue</button><button className="button secondary" onClick={translateAll} disabled={!needsTranslation || Boolean(busy)}>{busy === "translate_all" ? <LoaderCircle className="spin" size={17} /> : <Sparkles size={17} />} Translate all</button><button className="button secondary" onClick={onClose}>Cancel</button><button className="button primary" onClick={save} disabled={!allReady || Boolean(busy)}>{busy === "save" ? <LoaderCircle className="spin" size={17} /> : <Save size={17} />} Save all corrections</button></div>
        </footer>
      </section>
    </div>
  );
}
